import {
  getContentRange,
  UpdatableItemSync,
  UpdatablesHook,
} from "../../shared/customHoooks/useUpdatable";
import { COREError } from "./COREError";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CORETable, CORETableProps } from "./CORETable";
import {
  LocalStorageUntilLogoutKey,
  useLocalStorageUntilLogout,
} from "../../shared/customHoooks/useLocalStorageUntilLogout";
import {
  COREPagination,
  COREPaginationProps,
} from "../Navigation/COREPagination";
import { MRT_SortingState as MRTSortingState } from "material-react-table";
import { useLocalStorage, useDeepCompareEffect } from "react-use";
import { RowSelectionState, SortingState } from "@tanstack/table-core";
import { useSearchParams } from "react-router-dom";
import { writeURLParam } from "../../shared/customHoooks/urlSyncHelpers";

export type TableOptions = {
  pagination: Required<
    Pick<
      COREPaginationProps,
      "total" | "current" | "hideOnSinglePage" | "pageSize"
    >
  >;
};

export const initPagination = (initTablePageSize: number) => ({
  total: Number.MAX_SAFE_INTEGER,
  hideOnSinglePage: false,
  current: 1,
  pageSize: initTablePageSize,
});

export const initTableOptions = (initTablePageSize: number) => ({
  pagination: initPagination(initTablePageSize),
  filters: {},
});

export const updatePagination = (
  curTableOption: TableOptions | undefined,
  updates: Partial<TableOptions["pagination"]>,
  defaultPageSize: number
): TableOptions => {
  if (!curTableOption) {
    return { pagination: initPagination(defaultPageSize) };
  }

  const needsUpdate = Object.entries(updates).some(
    ([key, value]) =>
      curTableOption.pagination[
        key as keyof typeof curTableOption.pagination
      ] !== value
  );

  if (!needsUpdate) {
    return curTableOption;
  }

  return {
    pagination: {
      ...curTableOption.pagination,
      ...updates,
    },
  };
};

const convertToOrderFormat = (array?: MRTSortingState) => {
  if (array === undefined || array.length === 0) return {};
  const { id, desc } = array[0];
  return { order: `${id}.${desc ? "desc" : "asc"}.nullslast` };
};

export type CORETableHookParams<
  Params,
  RecordType extends Record<string, unknown>,
  Response,
  AddRequest = Response,
  AddResponse = Response,
  UpdateRequest = Response,
  UpdateResponse = Response,
  DeleteResponse = Response
> = {
  defaultPageSize?: number;
  hideLastPageButton?: boolean;
  /**
   * When provided, the server-side `TableOptions` pagination state is also
   * synced to this URL search param key, making the URL shareable. It is
   * consumed here and never reaches CORETable — for the client-side view state
   * (sorting, pagination, column visibility) use CORETable's `viewStateURLKey`
   * instead, and give it a different param key so the two do not fight over one.
   */
  urlKey?: string;
  onRowSelectionChange?: (rows: RowSelectionState) => void;
  markRowsAsSelected?: (dataSource: RecordType[]) => RowSelectionState;
  useUpdatables: (
    args: Params,
    tableOption?: TableOptions
  ) => UpdatablesHook<
    Response,
    AddRequest,
    AddResponse,
    UpdateRequest,
    UpdateResponse,
    DeleteResponse
  >;
  params: Params;
  transform: (
    record: UpdatableItemSync<
      Response,
      UpdateRequest,
      UpdateResponse,
      DeleteResponse
    >
  ) => RecordType;
} & Omit<
  CORETableProps<RecordType>,
  "data" | "enableGrouping" | "enablePagination"
> &
  (
    | { enableGrouping: true }
    | (({ enableGrouping: false } | { enableGrouping: undefined }) &
        (
          | { enableDownloadCSV: false; customDownloadCSVButton?: never }
          | { enableDownloadCSV: true; customDownloadCSVButton: ReactNode }
        ))
  );

type CORETableContentProps<
  Params,
  RecordType extends Record<string, unknown>,
  Response,
  AddRequest = Response,
  AddResponse = Response,
  UpdateRequest = Response,
  UpdateResponse = Response,
  DeleteResponse = Response
> = Omit<
  CORETableHookParams<
    Params,
    RecordType,
    Response,
    AddRequest,
    AddResponse,
    UpdateRequest,
    UpdateResponse,
    DeleteResponse
  >,
  "urlKey"
> & {
  tableOptions: TableOptions;
  setTableOptions: (
    updater: ((prev: TableOptions) => TableOptions) | TableOptions
  ) => void;
};

const CORETableContent = <
  Params,
  RecordType extends Record<string, unknown>,
  Response,
  AddRequest = Response,
  AddResponse = Response,
  UpdateRequest = Response,
  UpdateResponse = Response,
  DeleteResponse = Response
>({
  useUpdatables,
  params,
  transform,
  testID,
  customDownloadCSVButton,
  defaultPageSize = 20,
  hideLastPageButton = false,
  enableRowSelection,
  markRowsAsSelected,
  onRowSelectionChange,
  muiTableBodyRowProps,
  tableOptions,
  setTableOptions,
  ...props
}: CORETableContentProps<
  Params,
  RecordType,
  Response,
  AddRequest,
  AddResponse,
  UpdateRequest,
  UpdateResponse,
  DeleteResponse
>) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [initialSorting, setInitialSorting] = useLocalStorage<SortingState>(
    `${props.localStorageKey}-sorting`,
    []
  );
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);

  const initializedRef = useRef<{
    defaultSelection: Record<string, boolean>;
  }>({
    defaultSelection: {},
  });

  const { pagination } =
    tableOptions && Object.keys(tableOptions).length !== 0
      ? tableOptions
      : initTableOptions(defaultPageSize);

  const { current, pageSize } = pagination;

  const handleSortingChange = (
    update: SortingState | ((prev: SortingState) => SortingState)
  ) => {
    const isFunction = typeof update === "function";
    setSorting((prev) => {
      const result = isFunction ? update(prev ?? []) : update ?? [];
      setInitialSorting(result);
      return result;
    });
  };

  // Reset pagination to page 1 when params (filters) change
  useDeepCompareEffect(() => {
    setTableOptions((cur) =>
      updatePagination(cur, { current: 1 }, defaultPageSize)
    );
  }, [params]);

  const {
    sync,
    loading,
    error,
    headers,
    updatable: updatables,
  } = useUpdatables(
    { ...params, ...convertToOrderFormat(sorting) },
    !props.enableGrouping ? { pagination } : undefined
  );

  const dataSource = useMemo(() => {
    if (sync && transform) {
      return updatables.flatMap((u) => (u.sync ? [transform(u)] : []));
    }
    return [];
  }, [sync, transform, updatables]);

  useEffect(() => {
    if (dataSource.length > 0 && enableRowSelection && markRowsAsSelected) {
      const initial = markRowsAsSelected(dataSource);
      const newRowSelection: Record<string, boolean> = {};

      for (const [key, value] of Object.entries(initial)) {
        initializedRef.current.defaultSelection[key] = value;

        if (rowSelection[key] === undefined) {
          newRowSelection[key] = value;
        }
      }

      if (Object.keys(newRowSelection).length > 0) {
        setRowSelection((prev) => ({
          ...prev,
          ...newRowSelection,
        }));
      }
    }
  }, [dataSource, enableRowSelection, markRowsAsSelected, rowSelection]);

  if (error) {
    if (error.message.includes("416")) {
      setTableOptions((cur) =>
        updatePagination(cur, { current: 1 }, defaultPageSize)
      );
    }
    console.error(error);
    return <COREError title="Error loading table data" />;
  }

  let total = 0;
  if (headers) {
    const contentRange = getContentRange(headers);
    if (contentRange) {
      const totalRecords: string = contentRange.split("/")[1];
      total = totalRecords === "*" ? total : parseInt(totalRecords);
    }
  }

  const getChangedSelection = (
    current: RowSelectionState,
    defaults: Record<string, boolean>
  ): Record<string, boolean> => {
    const changed: Record<string, boolean> = {};
    const allKeys = new Set([
      ...Object.keys(current),
      ...Object.keys(defaults),
    ]);
    allKeys.forEach((key) => {
      const curVal = current[key] ?? false;
      const defVal = defaults[key] ?? false;
      if (curVal !== defVal) {
        changed[key] = curVal;
      }
    });
    return changed;
  };

  const filterToNonDefaultChanges = (
    changed: Record<string, boolean>,
    defaults: Record<string, boolean>
  ): Record<string, boolean> => {
    return Object.fromEntries(
      Object.entries(changed).filter(([key, value]) => value !== defaults[key])
    );
  };

  return (
    <CORETable
      {...props}
      testID={`${testID}-table`}
      isLoading={loading && !sync}
      data={dataSource}
      {...(enableRowSelection &&
        ({
          onRowSelectionChange: (updater) => {
            setRowSelection((prev) => {
              const updated =
                typeof updater === "function" ? updater(prev) : updater;

              const changed = getChangedSelection(
                updated,
                initializedRef.current.defaultSelection
              );
              const filtered = filterToNonDefaultChanges(
                changed,
                initializedRef.current.defaultSelection
              );

              onRowSelectionChange?.(filtered);

              return {
                ...updated,
                ...changed,
              };
            });
          },
          enableRowSelection: true,
          muiTableBodyRowProps: ({ row }) => ({
            onClick: () =>
              setRowSelection((prev) => {
                const next = {
                  ...prev,
                  [row.id]: !prev[row.id],
                };

                const changed = getChangedSelection(
                  next,
                  initializedRef.current.defaultSelection
                );

                const filtered = filterToNonDefaultChanges(
                  changed,
                  initializedRef.current.defaultSelection
                );

                onRowSelectionChange?.(filtered);

                return next;
              }),
            selected: rowSelection[row.id],
            sx: {
              cursor: "pointer",
            },
          }),
        } as Pick<
          CORETableProps<RecordType>,
          "enableRowSelection" | "muiTableBodyRowProps" | "onRowSelectionChange"
        >))}
      {...(!props.enableGrouping && {
        enablePagination: false,
        customDownloadCSVButton,
        defaultPageSize: pageSize,
        state: { ...props.state, sorting, rowSelection },
        manualSorting: true,
        onSortingChange: handleSortingChange,
        customPagination: (
          <COREPagination
            testID={`${testID}-pagination`}
            total={total}
            current={current}
            showSizeChanger
            pageSize={pageSize}
            hideLastPageButton={hideLastPageButton}
            onChange={(newPage, newPageSize) => {
              setTableOptions((cur) =>
                updatePagination(
                  cur,
                  { current: newPage, pageSize: newPageSize },
                  defaultPageSize
                )
              );
            }}
          />
        ),
      })}
    />
  );
};

export const CORETableHook = <
  Params,
  RecordType extends Record<string, unknown>,
  Response,
  AddRequest = Response,
  AddResponse = Response,
  UpdateRequest = Response,
  UpdateResponse = Response,
  DeleteResponse = Response
>({
  useUpdatables,
  params,
  transform,
  testID,
  customDownloadCSVButton,
  defaultPageSize = 20,
  hideLastPageButton = false,
  enableRowSelection,
  markRowsAsSelected,
  onRowSelectionChange,
  muiTableBodyRowProps,
  urlKey,
  ...props
}: CORETableHookParams<
  Params,
  RecordType,
  Response,
  AddRequest,
  AddResponse,
  UpdateRequest,
  UpdateResponse,
  DeleteResponse
>) => {
  const localStorageKey = props.localStorageKey as LocalStorageUntilLogoutKey;
  const [searchParams, setSearchParams] = useSearchParams();

  const [storedTableOptions, setStoredTableOptions] =
    useLocalStorageUntilLogout<TableOptions>({
      key: localStorageKey,
      initialValue: initTableOptions(defaultPageSize),
    });

  // Read URL synchronously — URL wins over localStorage (no useEffect needed)
  const urlRaw = urlKey ? searchParams.get(urlKey) : null;
  let urlTableOptions: TableOptions | undefined;
  if (urlRaw !== null) {
    try {
      urlTableOptions = JSON.parse(urlRaw) as TableOptions;
    } catch {
      // ignore malformed URL param — fall back to localStorage value
    }
  }

  // Priority: URL > localStorage > default
  const tableOptions = urlTableOptions ?? storedTableOptions;

  const handleSetTableOptions = useCallback(
    (updater: ((prev: TableOptions) => TableOptions) | TableOptions) => {
      const newValue =
        typeof updater === "function" ? updater(tableOptions) : updater;
      setStoredTableOptions(newValue);
      if (urlKey) {
        const next = JSON.stringify(newValue);
        if (searchParams.get(urlKey) !== next) {
          writeURLParam(urlKey, next, setSearchParams);
        }
      }
    },
    [tableOptions, setStoredTableOptions, searchParams, setSearchParams, urlKey]
  );

  return (
    <CORETableContent
      useUpdatables={useUpdatables}
      params={params}
      transform={transform}
      testID={testID}
      customDownloadCSVButton={customDownloadCSVButton}
      defaultPageSize={defaultPageSize}
      hideLastPageButton={hideLastPageButton}
      enableRowSelection={enableRowSelection}
      markRowsAsSelected={markRowsAsSelected}
      onRowSelectionChange={onRowSelectionChange}
      muiTableBodyRowProps={muiTableBodyRowProps}
      tableOptions={tableOptions}
      setTableOptions={handleSetTableOptions}
      {...props}
    />
  );
};
